import { FC } from "react";

import { Box, useMultiStyleConfig } from "@chakra-ui/react";
import { TFunction, useTranslation } from "next-i18next";

import Steps from "@components/Steps";
import type { TStep } from "@components/Steps/types";
import { ROUTES } from "@utils/constants";

import { OrderStep } from "../../constants";
import useOrderData from "../../context/useOrderData";
import { TOrderStepsData } from "../../types";

type TOrderStepsProps = {
  currentStep: OrderStep;
  isMobile?: boolean;
};

const getSteps = (t: TFunction, steps: TOrderStepsData, currentStep: OrderStep): TStep[] => {
  const packageInfoHref = [
    OrderStep.ShipmentDetails,
    OrderStep.SenderDetails,
    OrderStep.ReceiverDetails,
    OrderStep.Summary,
  ].includes(currentStep)
    ? ROUTES.ORDER
    : undefined;
  const shipmentDetailsHref = [OrderStep.Summary].includes(currentStep) ? ROUTES.ORDER_SHIPMENT : undefined;
  const senderDetailsHref = [OrderStep.ReceiverDetails, OrderStep.Summary].includes(currentStep)
    ? ROUTES.ORDER_SENDER
    : undefined;
  const receiverDetailsHref = [OrderStep.Summary].includes(currentStep) ? ROUTES.ORDER_RECEIVER : undefined;
  return [
    ...(steps[OrderStep.PackageInfo]
      ? [{ label: t("order-steps-package"), name: OrderStep.PackageInfo, href: packageInfoHref }]
      : []),
    ...(steps[OrderStep.ShipmentDetails]
      ? [{ label: t("order-steps-shipment_details"), name: OrderStep.ShipmentDetails, href: shipmentDetailsHref }]
      : []),
    ...(steps[OrderStep.SenderDetails]
      ? [{ label: t("order-steps-sender_details"), name: OrderStep.SenderDetails, href: senderDetailsHref }]
      : []),
    ...(steps[OrderStep.ReceiverDetails]
      ? [{ label: t("order-steps-receiver_details"), name: OrderStep.ReceiverDetails, href: receiverDetailsHref }]
      : []),
    ...(steps[OrderStep.Summary] ? [{ label: t("order-steps-summary_payment"), name: OrderStep.Summary }] : []),
    ...(steps[OrderStep.Success] ? [{ label: t("order-steps-success"), name: OrderStep.Success }] : []),
  ];
};

const OrderSteps: FC<TOrderStepsProps> = ({ currentStep, isMobile }) => {
  const styles = useMultiStyleConfig("Order", {});

  const { t } = useTranslation();

  const { steps } = useOrderData();

  const stepsList = getSteps(t, steps, currentStep);

  const stepIndex = stepsList.findIndex(({ name }) => name === currentStep);

  return (
    <Box sx={styles.orderSteps}>
      <Steps
        steps={stepsList}
        currentStepIndex={stepIndex}
        isVertical={!isMobile}
        isComplete={currentStep === OrderStep.Success}
      />
    </Box>
  );
};
export default OrderSteps;
